import React from "react";

import { faCode } from "@fortawesome/pro-regular-svg-icons";

import { Card } from "src/base-components/Card";
import { EmptyState } from "src/design-system/EmptyState";
import { DashboardContent } from "src/layout/DashboardContent";
import { SdkBundleModalShell } from "src/settings/sdks/components/SdkBundleModalShell";
import { SdkBundlesTable } from "src/settings/sdks/components/SdkBundlesTable";
import { SdkSettingsSubHeader } from "src/settings/sdks/components/SdkSettingsSubHeader";
import { useSdkSettingsSearchParams } from "src/settings/sdks/hooks/useSdkSettingsSearchParams";
import { MOCK_SDK_BUNDLES } from "src/settings/sdks/mocks/sdkBundles";
import { SdkSettingsTab } from "src/settings/sdks/searchParams";
import { SdkBundleRow } from "src/settings/sdks/types";
import { filterSdkBundleRows } from "src/settings/sdks/utils/filterRows";

const getModalTitle = (
  isCreateOpen: boolean,
  bundleToEdit: SdkBundleRow | undefined,
): string => {
  if (isCreateOpen) {
    return "Create SDK bundle";
  }
  if (bundleToEdit) {
    return `Edit ${bundleToEdit.name}`;
  }
  return "SDK bundle";
};

export const SdkBundlesPage: React.FC = () => {
  const {
    searchQuery,
    setSearch,
    entityId,
    isCreateOpen,
    isEditOpen,
    openCreateModal,
    openEditModal,
    closeModal,
  } = useSdkSettingsSearchParams(SdkSettingsTab.Bundles);

  const filteredRows = filterSdkBundleRows(MOCK_SDK_BUNDLES, searchQuery);
  const bundleToEdit = entityId
    ? MOCK_SDK_BUNDLES.find((row) => row.id === entityId)
    : undefined;
  // Derive open state from the URL intent alone so a stale `edit` id (no
  // matching row) still renders a dismissible modal that can clear the params.
  const isModalOpen = isCreateOpen || isEditOpen;

  return (
    <DashboardContent
      Header={
        <SdkSettingsSubHeader
          addDataLoc="sdk-bundles-add-button"
          addTooltip="Add SDK bundle"
          searchDataLoc="sdk-bundles-search"
          searchQuery={searchQuery}
          onAddClick={openCreateModal}
          onSearchChange={setSearch}
        />
      }
    >
      <Card
        className="flex h-full min-h-0 flex-col pt-1"
        dataLoc="sdk-bundles-card"
        fullWidth
        variant="inverted"
      >
        <Card.Content className="h-full min-h-0 flex-1">
          {filteredRows.length === 0 ? (
            <EmptyState
              dataLoc="sdk-bundles-empty-state"
              description={
                searchQuery
                  ? "Try adjusting your search query"
                  : "Create an SDK bundle to group scripts together"
              }
              headline={
                searchQuery ? "No matching SDK bundles" : "No SDK bundles yet"
              }
              icon={faCode}
            />
          ) : (
            <SdkBundlesTable
              rows={filteredRows}
              onRowClick={(row) => openEditModal(row.id)}
            />
          )}
        </Card.Content>
      </Card>

      <SdkBundleModalShell
        open={isModalOpen}
        title={getModalTitle(isCreateOpen, bundleToEdit)}
        onClose={closeModal}
      />
    </DashboardContent>
  );
};
